import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@mui/material';
import { FaApplePay } from 'react-icons/fa';
import './ApplePay.scss';

const MERCHANT_IDENTIFIER = '6bc7191a10c943228db359bee9d1723a8931fd4114544c806c889842';

function yieldPaint() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    });
  });
}

const ApplePay = ({ apiData }) => {
  const [showOverlay, setShowOverlay] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [checkingApplePay, setCheckingApplePay] = useState(false);

  const dismissOverlay = () => {
    setShowOverlay(false);
    setCheckingApplePay(false);
  };

  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        setShowOverlay(false);
        setApiError(null);
        setCheckingApplePay(false);
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  const handleApplePayClick = async () => {
    setApiError(null);
    setShowOverlay(true);

    const hasSession =
      typeof window !== 'undefined' &&
      typeof window.ApplePaySession !== 'undefined';
    console.log('hasSession', hasSession);

    if (!hasSession) {
      setApiError('Apple Pay is not available on this device/browser. Please try another payment method.');
      return;
    }

    setCheckingApplePay(true);
    await yieldPaint();

    let canPay = false;
    try {
      const capabilities = await window.ApplePaySession.applePayCapabilities(MERCHANT_IDENTIFIER);
      canPay = capabilities?.paymentCredentialStatus === 'paymentCredentialsAvailable';
      console.log('capabilities: ', capabilities);
    } catch {
      console.log('capabilities error');
      canPay = false;
    } finally {
      setCheckingApplePay(false);
    }

    if (!canPay) {
      setApiError('Apple Pay is not available on this device/browser. Please try another payment method.');
      return;
    }

    try {
      const orderId = new URLSearchParams(window.location.search).get('orderNo');
      const response = await fetch('/pay/securePayment/process/intermediate/paymentui', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          orderNo: orderId,
          mode: 'APPLE_PAY',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to start Apple Pay payment');
      }

      const data = await response.json();
      if (data.data.rurl) {
        window.location.href = data.data.rurl;
      } else {
        throw new Error('Missing redirect URL from server');
      }
    } catch (error) {
      console.error('Error starting Apple Pay payment:', error);
      setApiError('Please try again or choose another payment method.');
    }
  };

  const overlayFragment = showOverlay && (
    <>
      <div className="apple-pay-overlay__backdrop" aria-hidden="true" />
      <div
        className="apple-pay-overlay"
        role="dialog"
        aria-live="polite"
        aria-label="Apple Pay processing"
      >
        <div className="apple-pay-overlay__processing">
          {apiError ? (
            <>
              <div className="apple-pay-overlay__error-icon-wrap apple-pay-overlay__error-icon-wrap--desktop" aria-hidden="true">
                <svg
                  className="apple-pay-overlay__error-icon apple-pay-overlay__error-icon--desktop"
                  viewBox="0 0 64 64"
                  fill="none"
                >
                  <circle cx="32" cy="32" r="28" fill="#fee2e2" />
                  <circle cx="32" cy="32" r="19" fill="#fff" stroke="#fecaca" strokeWidth="2" />
                  <path d="M32 22v13" stroke="#b91c1c" strokeWidth="3" strokeLinecap="round" />
                  <circle cx="32" cy="40.8" r="2" fill="#b91c1c" />
                </svg>
              </div>
              <div className="apple-pay-overlay__error-icon-wrap apple-pay-overlay__error-icon-wrap--mobile" aria-hidden="true">
                <svg
                  className="apple-pay-overlay__error-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="7" x2="12" y2="13" />
                  <circle cx="12" cy="17" r="0.9" />
                </svg>
              </div>
              <p className="apple-pay-overlay__processing-title apple-pay-overlay__processing-title--error">
                Payment could not be processed
              </p>
              <div className="apple-pay-overlay__error-block" role="alert">
                <p className="apple-pay-error apple-pay-overlay__error-text">{apiError}</p>
              </div>
            </>
          ) : (
            <>
              <p className="apple-pay-overlay__processing-title">Processing your payment</p>
              <div className="apple-pay-step__progress-wrap" aria-hidden="true">
                <div className="apple-pay-step__progress-fill apple-pay-step__progress-fill--active apple-pay-step__progress-fill--short" />
              </div>
            </>
          )}
        </div>
        <div className="apple-pay-overlay__brand" aria-hidden="true">
          <FaApplePay className="apple-pay-overlay__brand-icon" aria-hidden />
        </div>
        <button
          className="apple-pay-overlay__close-desktop"
          type="button"
          aria-label="Close"
          onClick={dismissOverlay}
        >
          <span aria-hidden="true">&times;</span>
        </button>
        <button
          className="apple-pay-overlay__close-mobile"
          type="button"
          onClick={dismissOverlay}
        >
          Close
        </button>
      </div>
    </>
  );

  const desktopContent = (
    <div className="apple-pay-desktop">
      <div className="apple-pay-desktop__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="currentColor" className="apple-pay-svg">
          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
        </svg>
      </div>
      <p className="apple-pay-desktop__title">Complete payment with Apple Pay</p>
      <p className="apple-pay-desktop__sub">Fast, secure, and seamless checkout</p>
      <Button
        className="apple-pay-desktop__btn"
        variant="contained"
        onClick={handleApplePayClick}
        disableElevation
      >
        <span className="apple-pay-desktop__btn-text">
          Pay with{' '}
          <span className="apple-pay-desktop__btn-icon-pay">
            <svg viewBox="0 0 24 24" fill="currentColor" className="apple-pay-desktop__btn-icon" aria-hidden>
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
            </svg>
            Pay
          </span>
        </span>
      </Button>
    </div>
  );

  return (
    <div className="apple-pay">
      <div className="apple-pay-desktop-wrap">{desktopContent}</div>
      <div className="apple-pay-mobile-wrap">{desktopContent}</div>
      {overlayFragment}
    </div>
  );
};

ApplePay.propTypes = {
  apiData: PropTypes.shape({
    applePayRedirectUrl: PropTypes.string,
    callbackSuccessUrl: PropTypes.string,
  }),
};

ApplePay.defaultProps = {
  apiData: null,
};

export default ApplePay;
